import React, { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import cn from 'classnames';
import uuid from 'uuid/v4';
import { useDispatch, useSelector } from 'react-redux';
import { enablePageScroll, clearQueueScrollLocks } from 'scroll-lock';
import { useWindowSize } from 'react-use';

import { ReactComponent as Arrow } from './assets/arrow.svg';
import Button from '../Button';
import actions from '../../store/actions/meta';
import { messagesActions } from '../../store/actions';
import { getCookie, setCookie } from '../../utils';
import { carApi } from '../../api';
import { RESET_BUTTONS } from '../../store/actions/messages';

import s from './index.module.css';
import { getCurrentDialogId, getSenderId, getCarBrands, getIsMobile } from '../../store/selectors';

const customStyles = {
  container: (provided) => ({
    ...provided,
    outline: 'none',
  }),
  control: (provided) => ({
    ...provided,
    border: '1px solid #7E7E7E',
    borderColor: '#7E7E7E !important',
    boxShadow: 'none',
    borderRadius: '6px',
    outline: 'none',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontFamily: '"PT Root UI", sans-serif',
    fontSize: '17px',
    lineHeight: '21px',
    color: '#7E7E7E',
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: '20px',
    fontFamily: '"PT Root UI", sans-serif',
    fontSize: '17px',
    lineHeight: '21px',
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontSize: '14px',
    marginBottom: '10px',
    lineHeight: '18px',
    color: '#7E7E7E',
    paddingLeft: '18px',
    paddingTop: '5px',
    textTransform: 'none',
  }),
  option: (provided) => ({
    ...provided,
    cursor: 'pointer',
    fontSize: '17px',
    lineHeight: '21px',
    fontFamily: '"PT Root UI", sans-serif',
    paddingLeft: '40px',
  }),
  group: (provided, state) => ({
    ...provided,
    paddingBottom: '6px',
    borderTop: state.label === 'Все' ? '1px solid #DFDFDF' : '',
  }),
  menuList: (provided) => ({
    ...provided,
    '::-webkit-scrollbar-thumb': {
      background: '#C4C4C4',
      borderRadius: '5px',
    },
    '::-webkit-scrollbar': {
      width: '4px',
    },
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: '3px',
    marginBottom: '3px',
  }),
};

const CarBrandSelect = ({ submitText = 'Выбрать', onSubmit, messageId, chatWindowRef }) => {
  const dispatch = useDispatch();
  const carBrands = useSelector(getCarBrands);
  const senderId = useSelector(getSenderId);
  const dialogId = useSelector(getCurrentDialogId);
  const isMobile = useSelector(getIsMobile);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const carBrand = getCookie('carModel');
  const { width } = useWindowSize();
  const maxMenuSize = isMobile ? 200 : 440;

  const [value, setValue] = useState();
  const [popular, others] = carBrands.reduce(
    ([popular, others], item) => {
      const value = {
        value: item.key,
        label: item.name,
      };
      if (item.popular) {
        popular.push(value);
      } else {
        others.push(value);
      }

      return [popular, others];
    },
    [[], []],
  );

  const options = [
    {
      label: 'Популярные',
      options: popular,
    },
    {
      label: 'Все',
      options: others,
    },
  ];

  const Indicator = () => {
    return (
      <div
        className={cn(s.arrow, { [s.isActive]: isMenuOpen })}
        onTouchStart={() => {
          setMenuOpen(true);
        }}
      >
        <Arrow />
      </div>
    );
  };

  useEffect(() => {
    setValue([...popular, ...others].find((brand) => brand.value === carBrand));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carBrands]);

  const handleChange = (selectedOption) => {
    setValue(selectedOption);
    const wrapperEl = chatWindowRef.current && chatWindowRef.current.contentWrapperEl;
    if (wrapperEl) {
      requestAnimationFrame(() => {
        wrapperEl.scrollTo({
          top: wrapperEl.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  };

  const handleSubmit = () => {
    const text = `Марка автомобиля: ${value.label}`;
    const messageUuid = uuid();
    setCookie('carModel', value.value);
    dispatch(actions.setCarModel);
    carApi.selectCarBrand(messageId, value.value, text, dialogId, senderId, messageUuid);
    dispatch(messagesActions.setMessageFront(text, senderId, messageUuid));
    dispatch({ type: RESET_BUTTONS });
    if (onSubmit) {
      onSubmit();
    }
  };

  const handleOpenMenu = () => {
    setMenuOpen(true);
    setTimeout(() => {
      const dropdownEl = document.getElementById('react-select-2-listbox');
      if (isMobile) {
        clearQueueScrollLocks();
        enablePageScroll(dropdownEl);
      }
    }, 200);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  return (
    <div className={s.wrapper}>
      <ReactSelect
        components={{
          DropdownIndicator: Indicator,
          IndicatorSeparator: null,
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: '#EFEFEF',
          },
        })}
        value={value}
        noOptionsMessage={() => 'Не найдено'}
        styles={customStyles}
        placeholder="Марка"
        maxMenuHeight={maxMenuSize}
        menuPlacement={isMobile ? 'top' : 'auto'}
        onChange={handleChange}
        onMenuOpen={handleOpenMenu}
        onMenuClose={handleCloseMenu}
        options={options}
      />
      {value && (
        <Button onClick={handleSubmit} className={s.button} size="medium" view="button" background="blue">
          {submitText}
        </Button>
      )}
    </div>
  );
};

export default CarBrandSelect;
